// HMMM medical algorithm catalogue.
// Source: v2.1 collaboration prototype with @soresendiribelajar.
// Every content block ships both Indonesian (id) and English (en) copy — the
// UI selects the active language via LanguageProvider.
//
// Block schema
//   type: "flowTrack" | "branches" | "quad" | "panel" | "disclaimer"
//   All text fields shaped as { id, en }.

// -- Cardiovascular --------------------------------------------------------

const acls = {
  id: "acls",
  specialty: "cardiovascular",
  available: true,
  title: { id: "ACLS — Henti Jantung Dewasa", en: "ACLS — Adult Cardiac Arrest" },
  desc: { id: "VF/pVT & Asistol/PEA, siklus RJP 2 menit", en: "VF/pVT & asystole/PEA, 2-minute CPR cycles" },
  meta: {
    id: "Sumber: AHA Guidelines for CPR & ECC 2025 · Direview terakhir: Jul 2026",
    en: "Source: AHA Guidelines for CPR & ECC 2025 · Last reviewed: Jul 2026",
  },
  blocks: [
    {
      type: "flowTrack",
      steps: [
        {
          badge: "1",
          eyebrow: { id: "Aksi Awal", en: "Initial Action" },
          body: {
            id: "Pasien tidak respons & tidak bernapas normal → mulai RJP, beri oksigen, pasang monitor/defibrillator.",
            en: "Patient unresponsive & not breathing normally → start CPR, give oxygen, attach monitor/defibrillator.",
          },
        },
        {
          badge: "2",
          decision: true,
          eyebrow: { id: "Keputusan", en: "Decision" },
          body: {
            id: "Irama shockable? (VF / pVT)",
            en: "Shockable rhythm? (VF / pVT)",
          },
        },
      ],
    },
    {
      type: "imageGrid",
      // NOTE: swap the `src` values below with your real ECG image URLs.
      images: [
        {
          src: "https://litfl.com/wp-content/uploads/2018/08/ventricular-fibrillation-rhythm-strip-VF-shock-advised.jpg",
          alt: { id: "Contoh EKG Fibrilasi Ventrikel", en: "Ventricular Fibrillation ECG example" },
          caption: { id: "Ventricular Fibrillation (VF)", en: "Ventricular Fibrillation (VF)" },
          rhythm: "vf",
        },
        {
          src: "https://litfl.com/wp-content/uploads/2018/08/Monomorphic-ventricular-tachycardia-VT.jpg",
          alt: { id: "Contoh EKG Takikardia Ventrikel tanpa Nadi", en: "Pulseless Ventricular Tachycardia ECG example" },
          caption: { id: "Pulseless Ventricular Tachycardia (pVT)", en: "Pulseless Ventricular Tachycardia (pVT)" },
          rhythm: "vt",
        },
      ],
    },
    {
      type: "branches",
      left: {
        heading: { id: "Ya — VF / pVT (shockable)", en: "Yes — VF / pVT (shockable)" },
        color: "primary",
        steps: [
          {
            badge: "3a",
            body: {
              id: "Defibrilasi 1x (satu shock, bukan stacked shock) → lanjut RJP segera, jangan jeda cek nadi/irama.",
              en: "Single defibrillation (one shock, not stacked) → resume CPR immediately, no pause to check pulse/rhythm.",
            },
            list: {
              id: ["Dosis: 360 Joule untuk monofasik, atau 200 Joule untuk bifasik."],
              en: ["Dose: 360 Joules for Monophasic, or 200 Joules for Biphasic."],
            },
          },
          {
            badge: "4a",
            body: {
              id: "RJP 2 menit + akses IV/IO. Epinefrin diberikan setelah percobaan defibrilasi awal gagal.",
              en: "CPR 2 minutes + IV/IO access. Give epinephrine after the initial defibrillation attempt has failed.",
            },
          },
          {
            badge: "5a",
            body: {
              id: "Bila VF/pVT menetap setelah beberapa shock (persisting VF/pVT):",
              en: "If VF/pVT persists after several shocks (persisting VF/pVT):",
            },
            list: {
              id: [
                "Epinefrin tiap 3–5 menit",
                "Pertimbangkan amiodaron atau lidokain",
                "Pertimbangkan advanced airway + kapnografi",
              ],
              en: [
                "Epinephrine every 3–5 minutes",
                "Consider amiodarone or lidocaine",
                "Consider advanced airway + capnography",
              ],
            },
          },
        ],
        loop: { id: "↻ Ulangi siklus cek irama tiap 2 menit", en: "↻ Repeat rhythm-check cycle every 2 minutes" },
      },
      right: {
        heading: { id: "Tidak — Asistol / PEA (non-shockable)", en: "No — Asystole / PEA (non-shockable)" },
        color: "slate",
        steps: [
          {
            badge: "3b",
            body: {
              id: "RJP 2 menit + akses IV/IO. Beri epinefrin sesegera mungkin.",
              en: "CPR 2 minutes + IV/IO access. Give epinephrine as soon as possible.",
            },
          },
          {
            badge: "4b",
            body: {
              id: "Epinefrin tiap 3–5 menit. Pertimbangkan advanced airway + kapnografi. Cari & tangani penyebab reversibel.",
              en: "Epinephrine every 3–5 minutes. Consider advanced airway + capnography. Identify & treat reversible causes.",
            },
          },
        ],
        loop: { id: "↻ Ulangi siklus cek irama tiap 2 menit", en: "↻ Repeat rhythm-check cycle every 2 minutes" },
      },
    },
    {
      type: "flowTrack",
      steps: [
        {
          badge: "6",
          decision: true,
          eyebrow: { id: "Keputusan", en: "Decision" },
          body: {
            id: "Return of Spontaneous Circulation (ROSC)? — nadi teraba, kenaikan mendadak EtCO₂ (umumnya ≥40 mmHg), atau gelombang tekanan arteri spontan.",
            en: "Return of Spontaneous Circulation (ROSC)? — palpable pulse, sudden EtCO₂ rise (usually ≥40 mmHg), or spontaneous arterial waveform.",
          },
          bodyExtra: {
            id: "Ya → tata laksana pasca henti jantung, target MAP ≥65 mmHg, hindari hipotensi. Tidak → lanjutkan siklus RJP di atas.",
            en: "Yes → post-cardiac arrest care, target MAP ≥65 mmHg, avoid hypotension. No → continue the CPR cycle above.",
          },
        },
      ],
    },
    {
      type: "panel",
      heading: { id: "Penyebab Reversibel (H&T)", en: "Reversible Causes (H's & T's)" },
      tags: [
        { id: "Hipovolemia", en: "Hypovolaemia" },
        { id: "Hipoksia", en: "Hypoxia" },
        { id: "Ion Hidrogen (asidosis)", en: "Hydrogen ion (acidosis)" },
        { id: "Hipo/Hiperkalemia", en: "Hypo-/Hyperkalaemia" },
        { id: "Hipotermia", en: "Hypothermia" },
        { id: "Tension Pneumotoraks", en: "Tension pneumothorax" },
        { id: "Tamponade Jantung", en: "Cardiac tamponade" },
        { id: "Toksin", en: "Toxins" },
        { id: "Trombosis Paru", en: "Pulmonary thrombosis" },
        { id: "Trombosis Koroner", en: "Coronary thrombosis" },
      ],
    },
    {
      type: "panel",
      heading: { id: "Referensi Dosis Cepat", en: "Quick Dose Reference" },
      table: {
        headers: {
          id: ["Obat", "Dosis", "Catatan"],
          en: ["Drug", "Dose", "Notes"],
        },
        rows: [
          [
            { id: "Epinefrin", en: "Epinephrine" },
            { id: "1 mg IV/IO tiap 3–5 menit", en: "1 mg IV/IO every 3–5 min" },
            {
              id: "Non-shockable: berikan sesegera mungkin. Shockable: setelah shock awal gagal.",
              en: "Non-shockable: give ASAP. Shockable: after failed initial shock.",
            },
          ],
          [
            { id: "Amiodaron", en: "Amiodarone" },
            { id: "300 mg bolus, dosis ke-2: 150 mg", en: "300 mg bolus, 2nd dose: 150 mg" },
            { id: "Untuk VF/pVT refrakter", en: "For refractory VF/pVT" },
          ],
          [
            { id: "Lidokain", en: "Lidocaine" },
            { id: "1–1.5 mg/kg, dosis ke-2: 0.5–0.75 mg/kg", en: "1–1.5 mg/kg, 2nd dose: 0.5–0.75 mg/kg" },
            { id: "Alternatif bila amiodaron tidak tersedia", en: "Alternative if amiodarone unavailable" },
          ],
        ],
        footnote: {
          id: "Tidak direkomendasikan rutin tanpa indikasi spesifik: kalsium, natrium bikarbonat, magnesium, steroid, vasopressin sebagai pengganti epinefrin.",
          en: "Not routinely recommended without specific indication: calcium, sodium bicarbonate, magnesium, steroids, vasopressin as an epinephrine substitute.",
        },
      },
    },
    {
      type: "disclaimer",
      text: {
        id: "Alat bantu edukasi & referensi cepat, bukan pengganti penilaian klinis. Sesuaikan dengan kondisi pasien, protokol RS setempat, dan guideline terbaru.",
        en: "Educational quick reference only, not a substitute for clinical judgement. Adapt to patient condition, local hospital protocol and the latest guidelines.",
      },
    },
  ],
};

const hf = {
  id: "hf",
  specialty: "cardiovascular",
  available: true,
  title: { id: "Gagal Jantung Akut & Syok Kardiogenik", en: "Acute Heart Failure & Cardiogenic Shock" },
  desc: {
    id: "Klasifikasi hemodinamik & tata laksana awal",
    en: "Haemodynamic classification & initial management",
  },
  meta: {
    id: "Kerangka umum — sesuaikan protokol lokal · Direview terakhir: Jul 2026",
    en: "General framework — adapt local protocol · Last reviewed: Jul 2026",
  },
  blocks: [
    {
      type: "flowTrack",
      steps: [
        {
          badge: "1",
          eyebrow: { id: "Aksi Awal", en: "Initial Action" },
          body: {
            id: "Nilai ABC, tanda vital, SpO₂, EKG. Cari faktor pencetus (iskemia, aritmia, infeksi, ketidakpatuhan obat/diet, dll).",
            en: "Assess ABC, vitals, SpO₂, ECG. Look for precipitants (ischaemia, arrhythmia, infection, non-adherence, etc.).",
          },
        },
        {
          badge: "2",
          decision: true,
          eyebrow: { id: "Keputusan", en: "Decision" },
          body: {
            id: "Klasifikasi profil hemodinamik (Stevenson-Nohria) — kongesti (wet/dry) dan perfusi (warm/cold)?",
            en: "Classify haemodynamic profile (Stevenson-Nohria) — congestion (wet/dry) and perfusion (warm/cold)?",
          },
        },
      ],
    },
    {
      type: "quad",
      cells: [
        {
          title: { id: "Warm & Wet", en: "Warm & Wet" },
          desc: { id: "Kongesti, perfusi cukup — paling sering", en: "Congested, adequate perfusion — the most common" },
          accent: "sky",
        },
        {
          title: { id: "Cold & Wet", en: "Cold & Wet" },
          desc: {
            id: "Kongesti + hipoperfusi — curigai syok kardiogenik",
            en: "Congestion + hypoperfusion — suspect cardiogenic shock",
          },
          accent: "danger",
        },
        {
          title: { id: "Warm & Dry", en: "Warm & Dry" },
          desc: { id: "Kompensasi baik — optimalisasi obat oral", en: "Well compensated — optimise oral therapy" },
          accent: "teal",
        },
        {
          title: { id: "Cold & Dry", en: "Cold & Dry" },
          desc: { id: "Jarang — hati-hati hipovolemia relatif", en: "Rare — beware relative hypovolaemia" },
          accent: "amber",
        },
      ],
    },
    {
      type: "branches",
      left: {
        heading: { id: "Warm & Wet", en: "Warm & Wet" },
        color: "primary",
        steps: [
          {
            badge: "3a",
            body: {
              id: "Diuretik loop IV (mis. furosemide) sebagai lini pertama dekongesti.",
              en: "IV loop diuretic (e.g. furosemide) as first-line decongestion.",
            },
          },
          {
            badge: "4a",
            body: {
              id: "Bila hipertensi/kongesti berat dengan TD adekuat: tambahkan vasodilator IV (mis. nitrat) untuk perbaikan sesak.",
              en: "For hypertension / severe congestion with adequate BP: add IV vasodilator (e.g. nitrate) to relieve dyspnoea.",
            },
          },
          {
            badge: "5a",
            body: {
              id: "Evaluasi respons diuresis 1–2 jam; titrasi dosis bila respons kurang.",
              en: "Evaluate diuretic response at 1–2 h; titrate dose if response is inadequate.",
            },
          },
        ],
      },
      right: {
        heading: {
          id: "Cold & Wet (curiga syok kardiogenik)",
          en: "Cold & Wet (suspect cardiogenic shock)",
        },
        color: "danger",
        steps: [
          {
            badge: "3b",
            body: {
              id: "Pertimbangkan inotropik (dobutamin/milrinon) ± vasopresor (norepinefrin) bila hipotensi.",
              en: "Consider inotrope (dobutamine/milrinone) ± vasopressor (norepinephrine) for hypotension.",
            },
          },
          {
            badge: "4b",
            body: {
              id: "Diuresis hati-hati, hanya setelah perfusi membaik. Pertimbangkan rujuk ICU / dukungan sirkulasi mekanik.",
              en: "Cautious diuresis, only after perfusion improves. Consider ICU / mechanical circulatory support referral.",
            },
          },
        ],
      },
    },
    {
      type: "panel",
      heading: { id: "Pertimbangan Respirasi", en: "Respiratory Considerations" },
      paragraph: {
        id: "Distres napas berat / edema paru → pertimbangkan ventilasi non-invasif (CPAP/BiPAP). Target SpO₂ 94–98%.",
        en: "Severe respiratory distress / pulmonary oedema → consider non-invasive ventilation (CPAP/BiPAP). Target SpO₂ 94–98%.",
      },
    },
    {
      type: "panel",
      heading: { id: "Dosis Spesifik?", en: "Need Specific Doses?" },
      paragraph: {
        id: "Dosis, pengenceran, dan kecepatan infus obat-obat di atas (furosemide, dobutamin, norepinefrin) ada di menu Drug & Fluid → Vasopresor/Inotropik.",
        en: "Dose, dilution, and infusion rate for the drugs above (furosemide, dobutamine, norepinephrine) are in Drug & Fluid → Vasopressor/Inotrope.",
      },
    },
    {
      type: "disclaimer",
      text: {
        id: "Kerangka umum — protokol dosis dan pilihan obat spesifik dapat berbeda sesuai kebijakan RS setempat. Bukan pengganti penilaian klinis.",
        en: "General framework — specific dosing and drug choice may vary by local hospital policy. Not a substitute for clinical judgement.",
      },
    },
  ],
};

const acs = {
  id: "acs",
  specialty: "cardiovascular",
  available: true,
  title: { id: "ACS / STEMI — Jalur Reperfusi", en: "ACS / STEMI — Reperfusion Pathway" },
  desc: { id: "Jalur reperfusi", en: "Reperfusion pathway" },
  meta: {
    id: "Sumber: ACC/AHA Guideline for Management of Acute Coronary Syndromes 2025 · Direview terakhir: Jul 2026",
    en: "Source: ACC/AHA Guideline for Management of Acute Coronary Syndromes 2025 · Last reviewed: Jul 2026",
  },
  blocks: [
    {
      type: "flowTrack",
      steps: [
        {
          badge: "1",
          eyebrow: { id: "Aksi Awal", en: "Initial Action" },
          body: {
            id: "EKG 12 sadapan dalam 10 menit sejak kontak medis pertama. Aspirin loading (dikunyah), oksigen bila SpO₂ <90%, nitrat/morfin sesuai gejala.",
            en: "12-lead ECG within 10 min of first medical contact. Aspirin loading (chewed), oxygen if SpO₂ <90%, nitrates/morphine per symptoms.",
          },
        },
        {
          badge: "2",
          decision: true,
          eyebrow: { id: "Keputusan", en: "Decision" },
          body: { id: "Ada elevasi ST yang memenuhi kriteria STEMI?", en: "ST elevation meeting STEMI criteria?" },
          bodyExtra: {
            id: "Tidak → jalur NSTEMI/UA (algoritma terpisah, belum dibuat). Ya → lanjut di bawah.",
            en: "No → NSTEMI/UA pathway (separate algorithm, not yet published). Yes → continue below.",
          },
        },
        {
          badge: "3",
          decision: true,
          eyebrow: { id: "Keputusan", en: "Decision" },
          body: {
            id: "Fasilitas mampu PCI primer dalam waktu target?",
            en: "Facility capable of primary PCI within target time?",
          },
        },
      ],
    },
    {
      type: "branches",
      left: {
        heading: { id: "PCI Primer", en: "Primary PCI" },
        color: "primary",
        steps: [
          {
            badge: "4a",
            body: {
              id: "Target door-to-balloon ≤90 menit (presentasi langsung) atau ≤120 menit (perlu transfer).",
              en: "Target door-to-balloon ≤90 min (direct presentation) or ≤120 min (requires transfer).",
            },
          },
          {
            badge: "5a",
            body: {
              id: "DAPT: aspirin + ticagrelor/prasugrel (disarankan dibanding clopidogrel). Antikoagulan: UFH (atau bivalirudin).",
              en: "DAPT: aspirin + ticagrelor/prasugrel (preferred over clopidogrel). Anticoagulant: UFH (or bivalirudin).",
            },
          },
        ],
      },
      right: {
        heading: {
          id: "Fibrinolitik (bila delay >120 menit & onset <12 jam)",
          en: "Fibrinolytic (if delay >120 min & onset <12 h)",
        },
        color: "amber",
        steps: [
          {
            badge: "4b",
            body: {
              id: "Target door-to-needle ≤30 menit, diikuti transfer untuk angiografi (pendekatan farmako-invasif).",
              en: "Target door-to-needle ≤30 min, followed by transfer for angiography (pharmaco-invasive approach).",
            },
          },
          {
            badge: "5b",
            body: {
              id: "DAPT: aspirin + clopidogrel (wajib pada jalur fibrinolitik). Antikoagulan: enoxaparin/fondaparinux.",
              en: "DAPT: aspirin + clopidogrel (mandatory on the fibrinolytic pathway). Anticoagulant: enoxaparin/fondaparinux.",
            },
          },
        ],
      },
    },
    {
      type: "panel",
      heading: { id: "Red Flag", en: "Red Flag" },
      tags: [
        {
          id: "Hindari prasugrel bila riwayat stroke/TIA",
          en: "Avoid prasugrel if history of stroke/TIA",
        },
      ],
    },
    {
      type: "linkButton",
      to: "/heparin",
      label: { id: "Buka Protokol Heparin", en: "Open Heparin Protocol" },
      description: {
        id: "Hitung dosis awal & titrasi heparin (UFH) sesuai protokol ACS/VTE.",
        en: "Calculate initial dose & titrate heparin (UFH) per the ACS/VTE protocol.",
      },
    },
    {
      type: "disclaimer",
      text: {
        id: "Kerangka umum internasional — sesuaikan dengan ketersediaan fasilitas PCI, obat, dan jejaring rujukan RS/BPJS setempat.",
        en: "International general framework — adapt to local PCI availability, drugs, and referral network.",
      },
    },
  ],
};

const brady = {
  id: "brady",
  specialty: "cardiovascular",
  available: true,
  title: { id: "Bradikardia Simtomatik", en: "Symptomatic Bradycardia" },
  desc: { id: "Atropin, pacing transkutan", en: "Atropine, transcutaneous pacing" },
  meta: {
    id: "Sumber: AHA Guidelines for CPR & ECC 2025 · Direview terakhir: Jul 2026",
    en: "Source: AHA Guidelines for CPR & ECC 2025 · Last reviewed: Jul 2026",
  },
  blocks: [
    {
      type: "flowTrack",
      steps: [
        {
          badge: "1",
          eyebrow: { id: "Aksi Awal", en: "Initial Action" },
          body: {
            id: "Identifikasi bradikardia (umumnya HR <50/menit) simtomatik. Jaga jalan napas, O2 bila hipoksia, monitor, EKG 12 sadapan, akses IV.",
            en: "Identify symptomatic bradycardia (usually HR <50/min). Secure airway, O₂ if hypoxic, monitor, 12-lead ECG, IV access.",
          },
        },
        {
          badge: "2",
          decision: true,
          eyebrow: { id: "Keputusan", en: "Decision" },
          body: {
            id: "Ada tanda hipoperfusi (hipotensi, perubahan status mental, tanda syok, nyeri dada iskemik, tanda gagal jantung akut)?",
            en: "Signs of hypoperfusion (hypotension, altered mental status, shock, ischaemic chest pain, acute heart failure)?",
          },
          bodyExtra: {
            id: "Tidak → observasi ketat, cari penyebab (obat, iskemia, hipotiroid, gangguan elektrolit, tonus vagal). Ya → lanjut.",
            en: "No → close observation, look for cause (drugs, ischaemia, hypothyroid, electrolyte disturbance, vagal tone). Yes → continue.",
          },
        },
        {
          badge: "3",
          body: {
            id: "Atropin 1 mg IV/IO, dapat diulang tiap 3–5 menit, maksimal total 3 mg.",
            en: "Atropine 1 mg IV/IO, may repeat every 3–5 min, maximum total 3 mg.",
          },
        },
        {
          badge: "4",
          body: {
            id: "Bila tidak berespons / tidak mungkin berespons (mis. blok AV derajat tinggi):",
            en: "If unresponsive / unlikely to respond (e.g. high-grade AV block):",
          },
          list: {
            id: [
              "Pacing transkutan (segera bila tersedia), atau",
              "Infus dopamin 5–20 mcg/kg/menit, atau",
              "Infus epinefrin sebagai alternatif",
            ],
            en: [
              "Transcutaneous pacing (immediately if available), or",
              "Dopamine infusion 5–20 mcg/kg/min, or",
              "Epinephrine infusion as alternative",
            ],
          },
        },
      ],
    },
    {
      type: "panel",
      heading: { id: "Referensi Dosis Cepat", en: "Quick Dose Reference" },
      table: {
        headers: { id: ["Obat", "Dosis"], en: ["Drug", "Dose"] },
        rows: [
          [
            { id: "Atropin", en: "Atropine" },
            { id: "1 mg IV/IO, ulang q3–5 menit, maks 3 mg", en: "1 mg IV/IO, repeat q3–5 min, max 3 mg" },
          ],
          [
            { id: "Dopamin", en: "Dopamine" },
            { id: "Infus 5–20 mcg/kg/menit", en: "Infusion 5–20 mcg/kg/min" },
          ],
        ],
        footnote: {
          id: "Catatan: defibrilasi tidak digunakan untuk bradikardia (pasien masih bernadi).",
          en: "Note: defibrillation is not used for bradycardia (patient still has a pulse).",
        },
      },
    },
    {
      type: "disclaimer",
      text: {
        id: "Alat bantu edukasi & referensi cepat, bukan pengganti penilaian klinis.",
        en: "Educational quick reference only, not a substitute for clinical judgement.",
      },
    },
  ],
};

const tachy = {
  id: "tachy",
  specialty: "cardiovascular",
  available: true,
  title: { id: "Takikardia Stabil vs Tidak Stabil", en: "Stable vs Unstable Tachycardia" },
  desc: { id: "SVT, VT dengan nadi", en: "SVT, VT with pulse" },
  meta: {
    id: "Sumber: AHA Guidelines for CPR & ECC 2025 · Direview terakhir: Jul 2026",
    en: "Source: AHA Guidelines for CPR & ECC 2025 · Last reviewed: Jul 2026",
  },
  blocks: [
    {
      type: "flowTrack",
      steps: [
        {
          badge: "1",
          eyebrow: { id: "Aksi Awal", en: "Initial Action" },
          body: {
            id: "Nilai jalan napas, oksigen bila perlu, monitor, EKG 12 sadapan, akses IV.",
            en: "Assess airway, oxygen if needed, monitor, 12-lead ECG, IV access.",
          },
        },
        {
          badge: "2",
          decision: true,
          eyebrow: { id: "Keputusan", en: "Decision" },
          body: {
            id: "Pasien tidak stabil (hipotensi, perubahan status mental, tanda syok, nyeri dada iskemik, gagal jantung akut)?",
            en: "Patient unstable (hypotension, altered mental status, shock, ischaemic chest pain, acute heart failure)?",
          },
          bodyExtra: {
            id: "Ya → kardioversi tersinkronisasi segera. Tidak → lanjut jalur stabil di bawah.",
            en: "Yes → immediate synchronised cardioversion. No → follow the stable pathway below.",
          },
        },
      ],
    },
    {
      type: "panel",
      heading: {
        id: "Prosedur Kardioversi Tersinkronisasi",
        en: "Synchronised Cardioversion Procedure",
      },
      list: {
        id: [
          "Pertimbangkan sedasi pada pasien sadar.",
          "Aktifkan mode Sync (ON).",
          "Verifikasi marker sync tepat pada gelombang R.",
          "Pilih tingkat energi sesuai irama.",
          "Umumkan \"Clear\" dan berikan syok.",
        ],
        en: [
          "Consider sedation for conscious patients.",
          "Turn ON Sync mode.",
          "Verify sync markers on the R waves.",
          "Select the appropriate energy level.",
          "Announce \"Clear\" and deliver the shock.",
        ],
      },
    },
    {
      type: "branches",
      left: {
        heading: { id: "Kompleks Sempit Reguler (mis. SVT)", en: "Narrow, Regular Complex (e.g. SVT)" },
        color: "primary",
        steps: [
          { badge: "3a", body: { id: "Manuver vagal terlebih dahulu.", en: "Vagal manoeuvres first." } },
          { badge: "4a", body: { id: "Adenosin bila manuver vagal gagal.", en: "Adenosine if vagal manoeuvres fail." } },
        ],
      },
      right: {
        heading: { id: "Kompleks Lebar (Wide Complex Tachycardia)", en: "Wide Complex Tachycardia" },
        color: "amber",
        steps: [
          {
            badge: "3b",
            body: {
              id: "Bila reguler & monomorfik dengan etiologi tidak pasti → adenosin dapat dipertimbangkan. Amiodaron, prokainamid, atau sotalol juga dapat dipertimbangkan.",
              en: "If regular & monomorphic with uncertain aetiology → adenosine may be considered. Amiodarone, procainamide, or sotalol may also be considered.",
            },
          },
          {
            badge: "4b",
            decision: true,
            body: {
              id: "Jangan beri adenosin pada WCT tidak stabil, ireguler, atau polimorfik. Jangan beri verapamil/diltiazem pada WCT.",
              en: "Do not give adenosine in unstable, irregular, or polymorphic WCT. Do not give verapamil/diltiazem in WCT.",
            },
          },
        ],
      },
    },
    {
      type: "disclaimer",
      text: {
        id: "Alat bantu edukasi & referensi cepat, bukan pengganti penilaian klinis.",
        en: "Educational quick reference only, not a substitute for clinical judgement.",
      },
    },
  ],
};

const htn = {
  id: "htn",
  specialty: "cardiovascular",
  available: true,
  title: { id: "Krisis Hipertensi", en: "Hypertensive Crisis" },
  desc: { id: "Emergency vs urgency", en: "Emergency vs urgency" },
  meta: {
    id: "Sumber: kerangka umum konsensus internasional (ACC/AHA), sesuaikan panduan PERKI · Direview terakhir: Jul 2026",
    en: "Source: international consensus (ACC/AHA), adapt to local guidance · Last reviewed: Jul 2026",
  },
  blocks: [
    {
      type: "flowTrack",
      steps: [
        {
          badge: "1",
          eyebrow: { id: "Aksi Awal", en: "Initial Action" },
          body: {
            id: "Ukur ulang TD dengan manset sesuai ukuran. Nilai gejala kerusakan organ target (nyeri dada, sesak, defisit neurologis, penurunan kesadaran, gangguan penglihatan, nyeri punggung mendadak).",
            en: "Recheck BP with an appropriately sized cuff. Assess target-organ damage symptoms (chest pain, dyspnoea, neurologic deficit, altered consciousness, visual changes, sudden back pain).",
          },
        },
        {
          badge: "2",
          decision: true,
          eyebrow: { id: "Keputusan", en: "Decision" },
          body: {
            id: "Ada kerusakan organ target akut (otak, jantung, ginjal, mata, aorta)?",
            en: "Acute target-organ damage (brain, heart, kidney, eyes, aorta)?",
          },
        },
      ],
    },
    {
      type: "branches",
      left: {
        heading: { id: "Ya — Hypertensive Emergency", en: "Yes — Hypertensive Emergency" },
        color: "primary",
        steps: [
          {
            badge: "3b",
            body: {
              id: "Antihipertensi IV titrasi (mis. nikardipin atau nitrogliserin). Target penurunan MAP ~10–20% pada jam pertama.",
              en: "Titrated IV antihypertensive (e.g. nicardipine or nitroglycerin). Target MAP reduction ~10–20% in the first hour.",
            },
          },
        ],
      },
      right: {
        heading: { id: "Tidak — Hypertensive Urgency", en: "No — Hypertensive Urgency" },
        color: "slate",
        steps: [
          {
            badge: "3a",
            body: {
              id: "Turunkan TD bertahap dalam 24–48 jam dengan obat oral. Tidak perlu IV agresif. Observasi singkat / rawat jalan.",
              en: "Lower BP gradually over 24–48 h with oral therapy. No aggressive IV therapy. Brief observation / outpatient follow-up.",
            },
          },
        ],
      },
    },
    {
      type: "panel",
      heading: { id: "Kondisi Khusus dengan Target Berbeda", en: "Special Situations with Different Targets" },
      tags: [
        {
          id: "Diseksi aorta → turunkan TD & HR agresif & cepat",
          en: "Aortic dissection → lower BP & HR aggressively & rapidly",
        },
        {
          id: "Stroke iskemik akut → target lebih konservatif",
          en: "Acute ischaemic stroke → more conservative targets",
        },
        {
          id: "Preeklampsia berat/eklampsia → algoritma Obstetri (belum dibuat)",
          en: "Severe pre-eclampsia/eclampsia → Obstetrics algorithm (not yet published)",
        },
      ],
    },
    {
      type: "disclaimer",
      text: {
        id: "Target dan pilihan obat bisa bervariasi antar guideline (ACC/AHA vs ESC vs PERKI) — selaraskan dengan kebijakan RS kalian.",
        en: "Targets and drug choice vary across guidelines (ACC/AHA vs ESC vs local) — align with your hospital policy.",
      },
    },
  ],
};

export const CARDIOVASCULAR_ALGOS = { acls, hf, acs, brady, tachy, htn };
